import type {
  Activity,
  ActivityType,
  ChecklistPhase,
  ChecklistPriority,
  ChecklistTask,
  ChecklistTaskStatus,
  Event,
  EventStatus,
  Participant,
  ParticipantSource,
  ParticipantStatus,
  Partner,
  PartnerLog,
  PartnerStatus,
  Poster,
  PublicAgendaSnapshot,
  RegistrationFormField,
} from '@nx/domain';
import type { DocumentSnapshot, Timestamp } from 'firebase/firestore';

export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value && 'toDate' in value) {
    const result = (value as Timestamp).toDate();
    return result instanceof Date ? result : null;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function mapEventDoc(id: string, data: Record<string, unknown>): Event {
  return {
    id,
    title: String(data.title ?? ''),
    description: (data.description as string | undefined) ?? null,
    location: (data.location as string | undefined) ?? null,
    coverImageUrl: (data.coverImageUrl as string | undefined) ?? null,
    startDate: toDate(data.startDate) ?? new Date(),
    endDate: toDate(data.endDate) ?? new Date(),
    maxParticipants:
      typeof data.maxParticipants === 'number' ? data.maxParticipants : null,
    registrationDeadline: toDate(data.registrationDeadline),
    registrationFormFields: (data.registrationFormFields as RegistrationFormField[]) ?? [],
    publicAgendaToken: (data.publicAgendaToken as string | undefined) ?? null,
    status: (data.status as EventStatus) ?? 'draft',
    organizerId: String(data.organizerId ?? ''),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export function mapActivityDoc(
  eventId: string,
  id: string,
  data: Record<string, unknown>,
): Activity {
  return {
    id,
    eventId,
    date: String(data.date ?? ''),
    title: String(data.title ?? ''),
    type: (data.type as ActivityType) ?? 'other',
    startTime: String(data.startTime ?? ''),
    endTime: String(data.endTime ?? ''),
    venue: (data.venue as string | undefined) ?? null,
    partnerRefs: (data.partnerRefs as Activity['partnerRefs']) ?? [],
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
    createdAt: toDate(data.createdAt),
  };
}

export function mapParticipantDoc(
  eventId: string,
  id: string,
  data: Record<string, unknown>,
): Participant {
  return {
    id,
    eventId,
    name: String(data.name ?? ''),
    email: String(data.email ?? ''),
    company: (data.company as string | undefined) ?? null,
    jobTitle: (data.jobTitle as string | undefined) ?? null,
    phone: (data.phone as string | undefined) ?? null,
    tags: (data.tags as string[]) ?? [],
    status: (data.status as ParticipantStatus) ?? 'pending',
    checkedIn: Boolean(data.checkedIn),
    checkedInAt: toDate(data.checkedInAt),
    inviteEmailSentAt: toDate(data.inviteEmailSentAt),
    source: (data.source as ParticipantSource | undefined) ?? null,
    uid: (data.uid as string | undefined) ?? null,
    customFields: (data.customFields as Record<string, string>) ?? {},
    registeredAt: toDate(data.registeredAt),
  };
}

export function mapPartnerDoc(eventId: string, id: string, data: Record<string, unknown>): Partner {
  return {
    id,
    eventId,
    companyName: String(data.companyName ?? ''),
    contactName: (data.contactName as string | undefined) ?? null,
    contactEmail: (data.contactEmail as string | undefined) ?? null,
    contactPhone: (data.contactPhone as string | undefined) ?? null,
    logoUrl: (data.logoUrl as string | undefined) ?? null,
    logoDisplayOrder:
      typeof data.logoDisplayOrder === 'number' ? data.logoDisplayOrder : null,
    sponsorshipLevel: (data.sponsorshipLevel as string | undefined) ?? null,
    collaborationContent: (data.collaborationContent as string | undefined) ?? null,
    status: (data.status as PartnerStatus) ?? 'negotiating',
    contractFileUrl: (data.contractFileUrl as string | undefined) ?? null,
    contractDueDate: toDate(data.contractDueDate),
    monetaryAmount: typeof data.monetaryAmount === 'number' ? data.monetaryAmount : null,
    resourceExchangeNote: (data.resourceExchangeNote as string | undefined) ?? null,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export function mapPartnerLogDoc(
  eventId: string,
  id: string,
  data: Record<string, unknown>,
): PartnerLog {
  return {
    id,
    eventId,
    partnerId: String(data.partnerId ?? ''),
    content: String(data.content ?? ''),
    createdBy: String(data.createdBy ?? ''),
    createdAt: toDate(data.createdAt),
  };
}

export function mapChecklistDoc(
  eventId: string,
  id: string,
  data: Record<string, unknown>,
): ChecklistTask {
  return {
    id,
    eventId,
    phase: (data.phase as ChecklistPhase) ?? 'pre',
    title: String(data.title ?? ''),
    notes: (data.notes as string | undefined) ?? null,
    assigneeId: (data.assigneeId as string | undefined) ?? null,
    dueDate: toDate(data.dueDate),
    priority: (data.priority as ChecklistPriority) ?? 'medium',
    status: (data.status as ChecklistTaskStatus) ?? 'todo',
    tags: (data.tags as string[]) ?? [],
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
    createdAt: toDate(data.createdAt),
  };
}

export function mapPosterDoc(eventId: string, id: string, data: Record<string, unknown>): Poster {
  return {
    id,
    eventId,
    templateId: (data.templateId as string | undefined) ?? null,
    customTitle: (data.customTitle as string | undefined) ?? null,
    heroImageUrl: (data.heroImageUrl as string | undefined) ?? null,
    logoUrl: (data.logoUrl as string | undefined) ?? null,
    exportedImageUrl: (data.exportedImageUrl as string | undefined) ?? null,
    registrationLink: (data.registrationLink as string | undefined) ?? null,
    qrCodeUrl: (data.qrCodeUrl as string | undefined) ?? null,
    createdAt: toDate(data.createdAt),
  };
}

export function mapPublicAgendaDoc(data: Record<string, unknown>): PublicAgendaSnapshot {
  return data as unknown as PublicAgendaSnapshot;
}

export function snapshotData(snapshot: DocumentSnapshot): Record<string, unknown> {
  return (snapshot.data() ?? {}) as Record<string, unknown>;
}
